import {
  BaseQueryFn,
  MutationDefinition,
  EndpointBuilder,
  QueryDefinition,
} from '@reduxjs/toolkit/query';

import type { IRating } from 'interfaces/Rating.interface';
import type { ICollectionApiResponse } from 'services/api/api.types';
import type {
  IDeleteRatingItemArgs,
  IPostRatingsArgs,
  IPutRatingArgs,
  IRatingsCollectionArgs,
  IRatingsCollectionResponse,
} from './Rating.api.types';
import type { IId } from 'interfaces/IId.interface';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getRatings = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IRatingsCollectionArgs,
  BaseQueryFn,
  string,
  IRatingsCollectionResponse
> =>
  build.query<IRatingsCollectionResponse, IRatingsCollectionArgs>({
    providesTags: ['Rating'],
    query: (queryArg: IRatingsCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);

      return {
        url: `/api/ratings${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IRating>
    ): IRatingsCollectionResponse =>
      transformCollectionResponse<IRating>(response),
  });

export const postRatings = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPostRatingsArgs, BaseQueryFn, string, IRating> =>
  build.mutation({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['Rating'],
    query: (queryArg: IPostRatingsArgs) => ({
      url: '/api/ratings',
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const putRating = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPutRatingArgs, BaseQueryFn, string, IRating> =>
  build.mutation<IRating, IPutRatingArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['Rating'],
    query: (queryArg) => ({
      url: `/api/ratings/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const deleteRating = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IId, BaseQueryFn, string, unknown> =>
  build.mutation<unknown, IDeleteRatingItemArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['Rating'],
    query: (queryArg) => ({
      url: `api/ratings/${queryArg.id}`,
      method: 'DELETE',
    }),
  });
